// Admin section (requirement 11): usage dashboard, users, user detail, all blueprints, read-only blueprint view.
import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useCollection } from '@cloudscape-design/collection-hooks';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import DateRangePicker, { type DateRangePickerProps } from '@cloudscape-design/components/date-range-picker';
import Header from '@cloudscape-design/components/header';
import LineChart from '@cloudscape-design/components/line-chart';
import Link from '@cloudscape-design/components/link';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import TextFilter from '@cloudscape-design/components/text-filter';
import Toggle from '@cloudscape-design/components/toggle';
import type { AdminUserRow } from '@bda/shared';
import { api } from '../../api/client';
import { fetchAllPages, useBlueprint } from '../../api/hooks';
import { BlueprintView } from '../blueprints/BlueprintView';
import { setsEnabled } from '../../config';
import { saveBlob } from '../../lib/download';
import { daysAgo, formatDate, isoDay } from '../../lib/format';
import { errorMessage, useNotify } from '../../lib/notify';

// ---------- date range (7/30/90 days or custom; default 30) ----------

export function resolveRange(v: DateRangePickerProps.Value | null, now = new Date()): { from: string; to: string } {
  if (v?.type === 'absolute') return { from: v.startDate.slice(0, 10), to: v.endDate.slice(0, 10) };
  const days = v?.type === 'relative' ? v.amount : 30;
  return { from: daysAgo(days - 1, now), to: isoDay(now) };
}

const DEFAULT_RANGE: DateRangePickerProps.RelativeValue = { type: 'relative', key: '30d', amount: 30, unit: 'day' };

function useAdminRange() {
  const [value, setValue] = useState<DateRangePickerProps.Value | null>(DEFAULT_RANGE);
  const range = resolveRange(value);
  const picker = (
    <DateRangePicker
      value={value}
      onChange={(e) => setValue(e.detail.value ?? DEFAULT_RANGE)}
      relativeOptions={[
        { key: '7d', amount: 7, unit: 'day', type: 'relative' },
        { key: '30d', amount: 30, unit: 'day', type: 'relative' },
        { key: '90d', amount: 90, unit: 'day', type: 'relative' },
      ]}
      isValidRange={(r) => {
        if (r?.type === 'absolute') {
          const days = (Date.parse(r.endDate) - Date.parse(r.startDate)) / 86_400_000 + 1;
          if (!(days >= 1)) return { valid: false, errorMessage: 'End date must be after the start date.' };
          if (days > 366) return { valid: false, errorMessage: 'Choose a range of one year or less.' };
        }
        return { valid: true };
      }}
      dateOnly
      rangeSelectorMode="default"
      i18nStrings={{ ariaLabel: 'Date range' }}
    />
  );
  return { range, picker };
}

function ExportButton({
  kind,
  range,
  children,
}: {
  kind: 'users' | 'blueprints' | 'summary';
  range: { from: string; to: string };
  children: ReactNode;
}) {
  const notify = useNotify();
  return (
    <Button
      iconName="download"
      onClick={async () => {
        try {
          const { blob, fileName } = await api.adminExport(kind, range.from, range.to);
          saveBlob(blob, fileName);
        } catch (e) {
          notify.error(errorMessage(e));
        }
      }}
    >
      {children}
    </Button>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <Box variant="awsui-value-large">{value.toLocaleString()}</Box>
    </div>
  );
}

export function AdminDashboardPage() {
  const { range, picker } = useAdminRange();
  const q = useQuery({ queryKey: ['admin', 'summary', range], queryFn: () => api.adminSummary(range.from, range.to) });
  const s = q.data;
  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={`${range.from} to ${range.to}`}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {picker}
              <ExportButton kind="summary" range={range}>
                Export summary
              </ExportButton>
            </SpaceBetween>
          }
        >
          Usage
        </Header>
      }
    >
      {q.error && <Alert type="error">{errorMessage(q.error)}</Alert>}
      {!s ? (
        <Spinner size="large" />
      ) : (
        <SpaceBetween size="l">
          <Container header={<Header variant="h2">Users</Header>}>
            <ColumnLayout columns={5} variant="text-grid">
              <Stat label="Registered users" value={s.totalUsers} />
              <Stat label="Active in range" value={s.activeUsers} />
              <Stat label="Daily active" value={s.dau} />
              <Stat label="Weekly active" value={s.wau} />
              <Stat label="Monthly active" value={s.mau} />
            </ColumnLayout>
          </Container>
          <Container header={<Header variant="h2">Activity</Header>}>
            <ColumnLayout columns={4} variant="text-grid">
              <Stat label="Blueprints created" value={s.blueprintsCreated} />
              <Stat label="Versions published" value={s.versionsPublished} />
              <Stat label="Handoff packages" value={s.packagesCreated} />
              <Stat label="Pages processed" value={s.pages} />
              <Stat label="Runs started" value={s.runsStarted} />
              <Stat label="Runs succeeded" value={s.runsSucceeded} />
              <Stat label="Runs failed" value={s.runsFailed} />
            </ColumnLayout>
          </Container>
          <Container header={<Header variant="h2">Daily active users and runs</Header>}>
            <LineChart
              series={[
                {
                  title: 'Active users',
                  type: 'line',
                  data: s.series.map((d) => ({ x: new Date(`${d.date}T00:00:00Z`), y: d.activeUsers })),
                },
                {
                  title: 'Runs',
                  type: 'line',
                  data: s.series.map((d) => ({ x: new Date(`${d.date}T00:00:00Z`), y: d.runs })),
                },
              ]}
              xScaleType="time"
              xTitle="Date"
              yTitle="Count"
              height={260}
              ariaLabel="Daily active users and runs"
              i18nStrings={{
                xTickFormatter: (d) => (d as Date).toISOString().slice(5, 10),
                filterLabel: 'Filter displayed data',
                filterPlaceholder: 'Filter data',
                legendAriaLabel: 'Legend',
                chartAriaRoleDescription: 'line chart',
              }}
              empty={<Box>No activity in this range.</Box>}
            />
          </Container>
        </SpaceBetween>
      )}
    </ContentLayout>
  );
}

export function filterUsers(rows: AdminUserRow[], text: string): AdminUserRow[] {
  const t = text.trim().toLowerCase();
  return t ? rows.filter((r) => r.name.toLowerCase().includes(t) || r.email.toLowerCase().includes(t)) : rows;
}

export function AdminUsersPage() {
  const navigate = useNavigate();
  const { range, picker } = useAdminRange();
  const q = useQuery({ queryKey: ['admin', 'users', range], queryFn: () => api.adminUsers(range.from, range.to) });
  const { items, collectionProps, filterProps, filteredItemsCount } = useCollection(q.data?.items ?? [], {
    filtering: {
      filteringFunction: (item, text) => filterUsers([item], text).length > 0,
      empty: <Box>No users.</Box>,
      noMatch: <Box>No users match.</Box>,
    },
    sorting: { defaultState: { sortingColumn: { sortingField: 'lastSignInAt' }, isDescending: true } },
  });
  return (
    <Table
      {...collectionProps}
      header={
        <Header
          variant="h1"
          counter={`(${q.data?.items.length ?? 0})`}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {picker}
              <ExportButton kind="users" range={range}>
                Export users
              </ExportButton>
            </SpaceBetween>
          }
        >
          Users
        </Header>
      }
      loading={q.isLoading}
      items={items}
      trackBy="sub"
      filter={
        <TextFilter
          {...filterProps}
          filteringPlaceholder="Search name or email"
          filteringAriaLabel="Search users"
          countText={`${filteredItemsCount ?? 0} matches`}
        />
      }
      columnDefinitions={[
        {
          id: 'name',
          header: 'Name',
          sortingField: 'name',
          isRowHeader: true,
          cell: (u) => (
            <Link
              href={`/admin/users/${u.sub}`}
              onFollow={(e) => {
                e.preventDefault();
                navigate(`/admin/users/${u.sub}`, { state: u });
              }}
            >
              {u.name || u.email}
            </Link>
          ),
        },
        { id: 'email', header: 'Email', sortingField: 'email', cell: (u) => u.email },
        {
          id: 'roles',
          header: 'Roles',
          sortingComparator: (a, b) => a.roles.join().localeCompare(b.roles.join()),
          cell: (u) => u.roles.join(', ') || '-',
        },
        { id: 'status', header: 'Status', sortingField: 'enabled', cell: (u) => (u.enabled ? 'Enabled' : 'Disabled') },
        {
          id: 'first',
          header: 'First sign-in',
          sortingField: 'firstSignInAt',
          cell: (u) => (u.firstSignInAt ? formatDate(u.firstSignInAt) : 'Never'),
        },
        {
          id: 'last',
          header: 'Last sign-in',
          sortingField: 'lastSignInAt',
          cell: (u) => (u.lastSignInAt ? formatDate(u.lastSignInAt) : 'Never'),
        },
        { id: 'signins', header: 'Sign-ins', sortingField: 'signIns', cell: (u) => u.signIns },
        { id: 'bps', header: 'Blueprints', sortingField: 'blueprintsOwned', cell: (u) => u.blueprintsOwned },
        // Blueprint sets per user (blueprint-sets Req 12.7), only while sets are enabled.
        ...(setsEnabled()
          ? [{ id: 'sets', header: 'Sets', sortingField: 'setsOwned', cell: (u: AdminUserRow) => u.setsOwned ?? 0 }]
          : []),
        { id: 'runs', header: 'Runs', sortingField: 'runs', cell: (u) => u.runs },
        { id: 'pages', header: 'Pages', sortingField: 'pages', cell: (u) => u.pages },
      ]}
    />
  );
}

export const ACTION_LABELS: Record<string, string> = {
  SIGN_IN: 'Signed in',
  BLUEPRINT_CREATE: 'Created blueprint',
  BLUEPRINT_UPDATE: 'Saved blueprint',
  BLUEPRINT_PUBLISH: 'Published version',
  BLUEPRINT_ARCHIVE: 'Archived blueprint',
  BLUEPRINT_UNARCHIVE: 'Restored blueprint',
  BLUEPRINT_DUPLICATE: 'Duplicated blueprint',
  RUN_START: 'Started run',
  RUN_SUCCEEDED: 'Run succeeded',
  RUN_FAILED: 'Run failed',
  RUN_PREVIEW: 'Started preview',
  RUN_PREVIEW_DENIED: 'Preview denied',
  PACKAGE_CREATE: 'Created handoff package',
  SHARE_ADD: 'Shared package',
  SHARE_REVOKE: 'Revoked share',
  ADMIN_VIEW_TIMELINE: 'Viewed a user timeline',
  ADMIN_VIEW_BLUEPRINT: 'Viewed a blueprint',
  ADMIN_DOWNLOAD_SCHEMA: 'Downloaded a schema',
  ADMIN_EXPORT_CSV: 'Exported CSV',
  // Blueprint sets (blueprint-sets Req 12.4, 12.7).
  SET_CREATE: 'Created blueprint set',
  SET_UPDATE: 'Saved blueprint set',
  SET_PUBLISH: 'Published set version',
  SET_ARCHIVE: 'Archived blueprint set',
  SET_DUPLICATE: 'Duplicated blueprint set',
  SET_DENIED: 'Blueprint set request denied',
};

export function AdminUserPage() {
  const { sub } = useParams();
  const navigate = useNavigate();
  const { range, picker } = useAdminRange();
  const timeline = useQuery({
    queryKey: ['admin', 'timeline', sub, range],
    queryFn: () => api.adminTimeline(sub!, range.from, range.to),
  });
  const bps = useQuery({ queryKey: ['admin', 'userBlueprints', sub], queryFn: () => api.adminUserBlueprints(sub!) });
  return (
    <ContentLayout
      header={
        <Header variant="h1" actions={picker}>
          User activity
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Table
          header={
            <Header variant="h2" counter={`(${bps.data?.items.length ?? 0})`}>
              Blueprints
            </Header>
          }
          loading={bps.isLoading}
          items={bps.data?.items ?? []}
          empty={<Box>No blueprints.</Box>}
          columnDefinitions={[
            {
              id: 'name',
              header: 'Name',
              isRowHeader: true,
              cell: (b) => (
                <Link
                  href={`/admin/blueprints/${b.id}`}
                  onFollow={(e) => {
                    e.preventDefault();
                    navigate(`/admin/blueprints/${b.id}`);
                  }}
                >
                  {b.name}
                </Link>
              ),
            },
            { id: 'type', header: 'Document type', cell: (b) => b.documentType },
            {
              id: 'v',
              header: 'Latest version',
              cell: (b) => (b.latestVersion ? `v${b.latestVersion}` : 'Draft only'),
            },
            { id: 'archived', header: 'Archived', cell: (b) => (b.archived ? 'Yes' : 'No') },
          ]}
        />
        <Table
          header={<Header variant="h2">Timeline</Header>}
          loading={timeline.isLoading}
          items={timeline.data?.items ?? []}
          empty={<Box>No activity in this range.</Box>}
          columnDefinitions={[
            { id: 'at', header: 'When', cell: (a) => formatDate(a.at), isRowHeader: true },
            { id: 'action', header: 'Action', cell: (a) => ACTION_LABELS[a.action] ?? a.action },
            { id: 'bp', header: 'Blueprint', cell: (a) => a.blueprintName ?? '-' },
            {
              id: 'v',
              header: 'Version',
              cell: (a) => (a.version === undefined ? '-' : a.version === 'DRAFT' ? 'Draft' : `v${a.version}`),
            },
          ]}
        />
      </SpaceBetween>
    </ContentLayout>
  );
}

export function AdminBlueprintsPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [owner, setOwner] = useState('');
  const [docType, setDocType] = useState('');
  const [archived, setArchived] = useState(false);
  const list = useQuery({
    queryKey: ['admin', 'blueprints', q, owner, docType, archived],
    // Every page, so search and filters cover all blueprints (requirement 11.6).
    queryFn: () =>
      fetchAllPages((cursor) =>
        api.adminBlueprints({
          q: q || undefined,
          owner: owner || undefined,
          docType: docType || undefined,
          archived: archived ? 'true' : undefined,
          cursor,
        }),
      ),
  });
  const { range } = useAdminRange();
  return (
    <Table
      header={
        <Header
          variant="h1"
          counter={`(${list.data?.items.length ?? 0})`}
          actions={
            <ExportButton kind="blueprints" range={range}>
              Export blueprints
            </ExportButton>
          }
        >
          All blueprints
        </Header>
      }
      loading={list.isLoading}
      items={list.data?.items ?? []}
      trackBy="id"
      filter={
        <SpaceBetween direction="horizontal" size="m" alignItems="center">
          <TextFilter
            filteringText={q}
            onChange={(e) => setQ(e.detail.filteringText)}
            filteringPlaceholder="Search name or description"
            filteringAriaLabel="Search blueprints"
          />
          <TextFilter
            filteringText={owner}
            onChange={(e) => setOwner(e.detail.filteringText)}
            filteringPlaceholder="Owner email"
            filteringAriaLabel="Filter by owner"
          />
          <TextFilter
            filteringText={docType}
            onChange={(e) => setDocType(e.detail.filteringText)}
            filteringPlaceholder="Document type"
            filteringAriaLabel="Filter by document type"
          />
          <Toggle checked={archived} onChange={(e) => setArchived(e.detail.checked)}>
            Archived only
          </Toggle>
        </SpaceBetween>
      }
      empty={<Box>No blueprints.</Box>}
      columnDefinitions={[
        {
          id: 'name',
          header: 'Name',
          isRowHeader: true,
          cell: (b) => (
            <Link
              href={`/admin/blueprints/${b.id}`}
              onFollow={(e) => {
                e.preventDefault();
                navigate(`/admin/blueprints/${b.id}`);
              }}
            >
              {b.name}
            </Link>
          ),
        },
        { id: 'owner', header: 'Owner', cell: (b) => b.ownerEmail },
        { id: 'type', header: 'Document type', cell: (b) => b.documentType },
        { id: 'v', header: 'Latest version', cell: (b) => (b.latestVersion ? `v${b.latestVersion}` : 'Draft only') },
        { id: 'updated', header: 'Last modified', cell: (b) => formatDate(b.updatedAt) },
        { id: 'runs', header: 'Runs', cell: (b) => b.runCount },
        { id: 'archived', header: 'Archived', cell: (b) => (b.archived ? 'Yes' : 'No') },
      ]}
    />
  );
}

export function AdminBlueprintPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const q = useBlueprint(id);
  if (q.error) return <Alert type="error">{errorMessage(q.error)}</Alert>;
  if (!q.data) return <Spinner size="large" />;
  const latest = q.data.blueprint.latestVersion;
  const duplicate = async () => {
    try {
      const copy = await api.duplicate(q.data.blueprint.id);
      notify.success(`Copied into your library as "${copy.name}".`);
    } catch (e) {
      notify.error(errorMessage(e));
    }
  };
  const downloadSchema = async () => {
    if (!latest) return;
    try {
      const { blob, fileName } = await api.adminSchema(q.data.blueprint.id, latest);
      saveBlob(blob, fileName);
    } catch (e) {
      notify.error(errorMessage(e));
    }
  };
  return (
    <BlueprintView
      detail={q.data}
      showNotices={false}
      actions={
        <SpaceBetween direction="horizontal" size="xs">
          <Button onClick={() => void duplicate()}>Duplicate to my library</Button>
          <Button
            disabled={!latest}
            onClick={() => navigate(`/packages/new?blueprintId=${q.data.blueprint.id}&version=${latest}`)}
          >
            Create handoff package
          </Button>
          <Button iconName="download" disabled={!latest} onClick={() => void downloadSchema()}>
            Download schema
          </Button>
        </SpaceBetween>
      }
    />
  );
}
